/* eslint-disable no-unused-vars */
/* eslint-disable indent */
import React, { useState, useEffect, useCallback } from "react";
import { Row, Col, Card, Input, Table, Typography, Spin, message, Skeleton } from "antd";
import axios from "axios";


import { ReloadOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import constants from "../../../constants/constants";
import { formatIndianNumber, formatIndianCurrency } from "../../../utils";
import { DirectorDashboardService3 } from "../../../services/api/directorDashboardService3";

import "./style.scss";
import moment from "moment";

import { ReactComponent as HubInscanShipmentsIcon } from "../../../assests/images/DirectorDashboardSvg/HubInscanShipmentsIcon.svg";
import { ReactComponent as HubInscanBagIcon } from "../../../assests/images/DirectorDashboardSvg/HubInscanBag.svg";
import { ReactComponent as HubOutscanShipmentsIcon } from "../../../assests/images/DirectorDashboardSvg/HubOutScanShipment.svg";
import { ReactComponent as HubOutscanBagIcon } from "../../../assests/images/DirectorDashboardSvg/HubOutScanbag.svg";
import { ReactComponent as HubBagFinalizeIcon } from "../../../assests/images/DirectorDashboardSvg/HubBagFinalize.svg";

const { Title } = Typography;
const { ROLES } = constants;
const { ROLE_FUNCTIONALHEAD } = ROLES;

// KPI Icon SVG Paths


const CentralOpsDashboard = () => {
    const navigate = useNavigate();

    const [clientSearch, setClientSearch] = useState("");
    const [userRoles, setUserRoles] = useState([]);

    // Individual loading states for each section
    const [kpiDataLoading, setKpiDataLoading] = useState(false);

    // Separate loading states for each card
    const [shipmentCountsPreviousDayLoading, setShipmentCountsPreviousDayLoading] = useState(false);
    const [shipmentCountsCurrentMonthLoading, setShipmentCountsCurrentMonthLoading] = useState(false);
    const [deliveryDataPreviousDayLoading, setDeliveryDataPreviousDayLoading] = useState(false);

    // State for Today Hub Operations
    const [kpiData, setKpiData] = useState({
        todayHubInscanShipments: 0,
        todayHubInscanBags: 0,
        todayHubOutscanShipments: 0,
        todayHubOutscanBags: 0,
        todayHubFinalizeBags: 0,
    });



    // State for Shipment Counts
    const [shipmentCountsToday, setShipmentCountsToday] = useState([]);
    const [shipmentCountsRange, setShipmentCountsRange] = useState([]);
    const [shipmentCountsTodayTimeZone, setShipmentCountsTodayTimeZone] = useState("");
    const [shipmentCountsRangeTimeZone, setShipmentCountsRangeTimeZone] = useState("");

    // State for Delivery Data
    const [deliveryDataToday, setDeliveryDataToday] = useState([]);
    const [deliveryDataRange, setDeliveryDataRange] = useState([]);
    const [deliveryDataTodayTimeZone, setDeliveryDataTodayTimeZone] = useState("");

    useEffect(() => {
        const userinfo = JSON.parse(localStorage.getItem("userinfo") || "{}");
        setUserRoles(userinfo?.roles || []);
    }, []);

    // Helper function to parse and format numbers
    const parseAndFormat = useCallback((value) => {
        if (!value) return "0";
        const numValue = parseInt(value.toString().replace(/,/g, "").trim(), 10);
        return isNaN(numValue) ? "0" : formatIndianNumber(numValue);
    }, []);

    // Helper function to format label from figureSubtype
    const formatLabel = useCallback((subtype) => {
        // Convert camelCase to Title Case
        if (subtype === "totalShipmentCount") return "Total";
        if (subtype === "totalDelivery") return "Total Shipments";

        // Capitalize first letter and add spaces before capital letters
        return subtype.replace(/([A-Z])/g, " $1").replace(/^./, str => str.toUpperCase()).trim();
    }, []);

    // Helper function to process Previous Day API response
    const processPreviousDayData = useCallback((responseData) => {
        if (!responseData?.data) return;

        const dataArray = responseData.data.data || [];

        // Group data by figureType
        const shipmentCountData = dataArray.filter(item => item.figureType === "shipment_count");
        const drsData = dataArray.filter(item => item.figureType === "drs");

        // Process Shipment Counts - Previous Day
        if (shipmentCountData.length > 0) {
            const shipmentItems = shipmentCountData.map((item, index) => ({
                key: `shipment-${index + 1}`,
                label: item.figureSubtype,
                value: parseAndFormat(item.value || 0),
                rawValue: item.value || 0,
            }));
            // Sort: non-total items first, then total at the end
            const sortedShipment = [
                ...shipmentItems.filter(item => item.label !== "Total"),
                ...shipmentItems.filter(item => item.label === "Total")
            ];
            setShipmentCountsToday(sortedShipment);
            // Set timezone if available from response
            if (responseData.data.fromDate) {
                const dateStr = responseData.data.fromDate;
                const formattedDate = `${dateStr.substring(6, 8)}/${dateStr.substring(4, 6)}/${dateStr.substring(0, 4)}`;
                setShipmentCountsTodayTimeZone(formattedDate);
            }
        }

        // Process Delivery Data (DRS) - Previous Day
        if (drsData.length > 0) {
            const deliveryItems = drsData.map((item, index) => {
                let label = item.figureSubtype;

                return {
                    key: `delivery-${index + 1}`,
                    label: label,
                    value: parseAndFormat(item.value || 0),
                    rawValue: item.value || 0,
                };
            });
            // Sort: regular items first, then "Total" (second-to-last), then "Total DRS" at the end
            const sortedDelivery = [
                ...deliveryItems.filter(item => item.label !== "Total DRS" && item.label !== "Total"),
                ...deliveryItems.filter(item => item.label === "Total"),
                ...deliveryItems.filter(item => item.label === "Total DRS")
            ];
            setDeliveryDataToday(sortedDelivery);
            // Set timezone if available from response
            if (responseData.data.fromDate) {
                const dateStr = responseData.data.fromDate;
                const formattedDate = `${dateStr.substring(6, 8)}/${dateStr.substring(4, 6)}/${dateStr.substring(0, 4)}`;
                setDeliveryDataTodayTimeZone(formattedDate);
            }
        }
    }, [formatLabel, parseAndFormat]);

    // Helper function to process Current Month API response
    const processCurrentMonthData = useCallback((responseData) => {
        if (!responseData?.data) return;

        const dataArray = responseData.data.data || [];

        // Group data by figureType
        const shipmentCountData = dataArray.filter(item => item.figureType === "shipment_count");

        // Process Shipment Counts - Current Month
        if (shipmentCountData.length > 0) {
            const shipmentItems = shipmentCountData.map((item, index) => ({
                key: `shipment-range-${index + 1}`,
                label: item.figureSubtype,
                value: parseAndFormat(item.value || 0),
                rawValue: item.value || 0,
            }));
            // Sort: non-total items first, then total at the end
            const sortedShipment = [
                ...shipmentItems.filter(item => item.label !== "Total"),
                ...shipmentItems.filter(item => item.label === "Total")
            ];
            setShipmentCountsRange(sortedShipment);
            // Set timezone if available from response
            if (responseData.data.fromDate && responseData.data.toDate) {
                const fromDateStr = responseData.data.fromDate;
                const toDateStr = responseData.data.toDate;
                const formattedFromDate = `${fromDateStr.substring(6, 8)}/${fromDateStr.substring(4, 6)}/${fromDateStr.substring(0, 4)}`;
                const formattedToDate = `${toDateStr.substring(6, 8)}/${toDateStr.substring(4, 6)}/${toDateStr.substring(0, 4)}`;
                const dateRange = `${formattedFromDate} - ${formattedToDate}`;
                setShipmentCountsRangeTimeZone(dateRange);
            }
        }
    }, [formatLabel, parseAndFormat]);

    // Fetch all dashboard data - each API updates UI as soon as response arrives
    const fetchDashboardData = useCallback(async (signal) => {
        // Set loading states for each section
        setKpiDataLoading(true);
        setShipmentCountsPreviousDayLoading(true);
        setShipmentCountsCurrentMonthLoading(true);
        setDeliveryDataPreviousDayLoading(true);

        const options = { signal };

        // Fetch HubOps data - update immediately when response arrives
        DirectorDashboardService3.getHubOpsData(options)
            .then((hubOpsResponse) => {
                if (hubOpsResponse?.data) {
                    const data = hubOpsResponse.data;
                    setKpiData({
                        todayHubInscanShipments: data.inscanShipmentCount?.value || 0,
                        todayHubInscanBags: data.inscanBagCount?.value || 0,
                        todayHubOutscanShipments: data.outscanShipmentCount?.value || 0,
                        todayHubOutscanBags: data.outscanBagCount?.value || 0,
                        todayHubFinalizeBags: data.finalizedBagCount?.value || 0,
                    });
                }
            })
            .catch((error) => {
                if (axios.isCancel(error)) return;
                console.error("Error fetching HubOps data:", error);
            })
            .finally(() => {
                if (!signal?.aborted) setKpiDataLoading(false);
            });

        // Fetch Business Statistics Previous Day Data - update immediately when response arrives
        DirectorDashboardService3.getBusinessStatisticsPreviousDay(options)
            .then((previousDayResponse) => {
                processPreviousDayData(previousDayResponse);
            })
            .catch((error) => {
                if (axios.isCancel(error)) return;
                console.error("Error fetching Business Statistics Previous Day Data:", error);
            })
            .finally(() => {
                if (!signal?.aborted) {
                    // Hide loading for Previous Day cards
                    setShipmentCountsPreviousDayLoading(false);
                    setDeliveryDataPreviousDayLoading(false);
                }
            });

        // Fetch Business Statistics Current Month Data - update immediately when response arrives
        DirectorDashboardService3.getBusinessStatisticsCurrentMonth(options)
            .then((currentMonthResponse) => {
                processCurrentMonthData(currentMonthResponse);
            })
            .catch((error) => {
                if (axios.isCancel(error)) return;
                console.error("Error fetching Business Statistics Current Month Data:", error);
            })
            .finally(() => {
                if (!signal?.aborted) {
                    // Hide loading for Current Month cards
                    setShipmentCountsCurrentMonthLoading(false);
                }
            });
    }, [processPreviousDayData, processCurrentMonthData]);

    useEffect(() => {
        const controller = new AbortController();
        if (userRoles.includes(ROLE_FUNCTIONALHEAD)) {
            fetchDashboardData(controller.signal);
        }
        return () => controller.abort();
    }, [userRoles, fetchDashboardData]);


    // Refresh HubOps data (KPI cards) - First refresh cache, then fetch data
    const handleRefreshHubOps = useCallback(() => {
        setKpiDataLoading(true);

        // First, refresh the cache
        DirectorDashboardService3.refreshCacheHubOpsData()
            .then((refreshResponse) => {
                // Check if cache refresh was successful
                if (refreshResponse?.data?.status === "SUCCESS" || refreshResponse?.data?.responseStatus === "SUCCESS") {
                    // After successful cache refresh, fetch the updated data
                    return DirectorDashboardService3.getHubOpsData();
                } else {
                    throw new Error(refreshResponse?.data?.message || "Cache refresh failed");
                }
            })
            .then((hubOpsResponse) => {
                if (hubOpsResponse?.data) {
                    const data = hubOpsResponse.data;
                    setKpiData({
                        todayHubInscanShipments: data.inscanShipmentCount?.value || 0,
                        todayHubInscanBags: data.inscanBagCount?.value || 0,
                        todayHubOutscanShipments: data.outscanShipmentCount?.value || 0,
                        todayHubOutscanBags: data.outscanBagCount?.value || 0,
                        todayHubFinalizeBags: data.finalizedBagCount?.value || 0,
                    });
                }
            })
            .catch((error) => {
                console.error("Error refreshing HubOps data:", error);
                message.error(error?.response?.data?.message || error?.message || "Failed to refresh data");
            })
            .finally(() => {
                setKpiDataLoading(false);
            });
    }, []);

    // Refresh Shipment Counts - Previous Day
    const handleRefreshShipmentCountsPreviousDay = useCallback(() => {
        // Only set loading for this specific card
        setShipmentCountsPreviousDayLoading(true);
        // Ensure other Previous Day cards are not loading
        setDeliveryDataPreviousDayLoading(false);

        DirectorDashboardService3.refreshCacheBusinessStatisticsPreviousDay()
            .then((refreshResponse) => {
                if (refreshResponse?.data?.status === "SUCCESS" || refreshResponse?.data?.responseStatus === "SUCCESS") {
                    return DirectorDashboardService3.getBusinessStatisticsPreviousDay();
                } else {
                    throw new Error(refreshResponse?.data?.message || "Cache refresh failed");
                }
            })
            .then((previousDayResponse) => {
                processPreviousDayData(previousDayResponse);
            })
            .catch((error) => {
                console.error("Error refreshing Shipment Counts Previous Day Data:", error);
                message.error(error?.response?.data?.message || error?.message || "Failed to refresh data");
            })
            .finally(() => {
                setShipmentCountsPreviousDayLoading(false);
            });
    }, [processPreviousDayData]);

    // Refresh Shipment Counts - Current Month
    const handleRefreshShipmentCountsCurrentMonth = useCallback(() => {
        // Only set loading for this specific card
        setShipmentCountsCurrentMonthLoading(true);

        DirectorDashboardService3.refreshCacheBusinessStatisticsCurrentMonth()
            .then((refreshResponse) => {
                if (refreshResponse?.data?.status === "SUCCESS" || refreshResponse?.data?.responseStatus === "SUCCESS") {
                    return DirectorDashboardService3.getBusinessStatisticsCurrentMonth();
                } else {
                    throw new Error(refreshResponse?.data?.message || "Cache refresh failed");
                }
            })
            .then((currentMonthResponse) => {
                processCurrentMonthData(currentMonthResponse);
            })
            .catch((error) => {
                console.error("Error refreshing Shipment Counts Current Month Data:", error);
                message.error(error?.response?.data?.message || error?.message || "Failed to refresh data");
            })
            .finally(() => {
                setShipmentCountsCurrentMonthLoading(false);
            });
    }, [processCurrentMonthData]);

    // Refresh Delivery Data - Previous Day
    const handleRefreshDeliveryDataPreviousDay = useCallback(() => {
        // Only set loading for this specific card
        setDeliveryDataPreviousDayLoading(true);
        // Ensure other Previous Day cards are not loading
        setShipmentCountsPreviousDayLoading(false);

        DirectorDashboardService3.refreshCacheBusinessStatisticsPreviousDay()
            .then((refreshResponse) => {
                if (refreshResponse?.data?.status === "SUCCESS" || refreshResponse?.data?.responseStatus === "SUCCESS") {
                    return DirectorDashboardService3.getBusinessStatisticsPreviousDay();
                } else {
                    throw new Error(refreshResponse?.data?.message || "Cache refresh failed");
                }
            })
            .then((previousDayResponse) => {
                processPreviousDayData(previousDayResponse);
            })
            .catch((error) => {
                console.error("Error refreshing Delivery Data:", error);
                message.error(error?.response?.data?.message || error?.message || "Failed to refresh data");
            })
            .finally(() => {
                setDeliveryDataPreviousDayLoading(false);
            });
    }, [processPreviousDayData]);

    // Refresh all data (for initial load)
    const handleRefresh = () => {
        fetchDashboardData();
    };

    const handleViewZoneWise = (e, type = "today", reportType = "Piece Rate", reportCategory = "Shipment Counts", businessType = null) => {
        if (e) {
            e.preventDefault();
            e.stopPropagation();
        }

        // Use dates directly from API response (stored in state)
        let dateToPass, endDateToPass;
        if (type === "today") {
            // Use the date from API response if available
            if (shipmentCountsTodayTimeZone) {
                // Using shipment date as fallback or just today.
                dateToPass = moment().subtract(1, "day").format("YYYY-MM-DD");
            } else {
                // Fallback: use previous day
                dateToPass = moment().subtract(1, "day").format("YYYY-MM-DD");
            }
        } else {
            // For range, use dates directly from API response
            if (shipmentCountsRangeTimeZone) {
                // Fallback to month start
                dateToPass = moment().startOf("month").format("YYYY-MM-DD");
                endDateToPass = moment().format("YYYY-MM-DD");
            } else {
                // Fallback: use month start to today
                dateToPass = moment().startOf("month").format("YYYY-MM-DD");
                endDateToPass = moment().format("YYYY-MM-DD");
            }
        }

        const routeMap = {
            "Shipment Counts": "/central-ops/zone-wise-shipment-counts",
            "Delivery Data": "/central-ops/zone-wise-delivery-data",
        };
        navigate(routeMap[reportCategory] || "/central-ops/zone-wise-shipment-counts", {
            state: {
                type,
                reportType,
                reportCategory,
                date: dateToPass, // Start date in "YYYY-MM-DD" format from API response
                endDate: type === "range" ? endDateToPass : undefined, // End date for range type from API response
                businessType, // Pass selected business type
            },
        });
    };

    // Handle click on table row (Cash, Account, DP, Piece Rate, etc.)
    const handleTableRowClick = (record, reportCategory, dataType) => {
        // Don't navigate if it's the Total row
        if (record.label === "Total") {
            return;
        }

        // Map label to business type name - use label directly as businessType
        const businessType = record.label;

        // Use dates directly from API response (stored in state)
        let dateToPass, endDateToPass;

        if (dataType === "today") {
            // Use the date from API response if available
            if (shipmentCountsTodayTimeZone) {
                dateToPass = moment().subtract(1, "day").format("YYYY-MM-DD");
            } else {
                // Fallback: use previous day
                dateToPass = moment().subtract(1, "day").format("YYYY-MM-DD");
            }
        } else {
            // For range
            // Fallback: use month start to today
            dateToPass = moment().startOf("month").format("YYYY-MM-DD");
            endDateToPass = moment().format("YYYY-MM-DD");
        }

        // Navigate to ZoneList route with selected business type
        navigate("/central-ops/zone-list", {
            state: {
                type: dataType,
                reportType: "Piece Rate",
                reportCategory: reportCategory,
                date: dateToPass, // Start date in "YYYY-MM-DD" format from API response
                endDate: dataType === "range" ? endDateToPass : undefined, // End date for range type from API response
                businessType: businessType,
            },
        });
    };

    // Check if user has ROLE_FUNCTIONALHEAD access
    if (!userRoles.includes(ROLE_FUNCTIONALHEAD)) {
        return (
            <div className="director-dashboard-access-denied">
                <Card>
                    <Title level={4}>Access Denied</Title>
                    <p>You do not have permission to access this dashboard.</p>
                </Card>
            </div>
        );
    }

    // Use TimeZone from API if available, otherwise use current date
    const todayDate = moment().format("DD/MM/YYYY");
    const dateRange = (() => {
        const rangeStartDate = moment().startOf("month").format("DD/MM/YYYY");
        const rangeEndDate = moment().format("DD/MM/YYYY");
        return `${rangeStartDate} - ${rangeEndDate}`;
    })();

    // Use TimeZone from API for Shipment Counts if available
    const shipmentTodayDate = shipmentCountsTodayTimeZone || todayDate;
    const shipmentDateRange = shipmentCountsRangeTimeZone || dateRange;

    // Table columns for data tables
    const dataTableColumns = [
        {
            title: "",
            dataIndex: "label",
            key: "label",
            width: "60%",
        },
        {
            title: "",
            dataIndex: "value",
            key: "value",
            width: "40%",
            align: "right",
        },
    ];

    // Helper function to create columns with clickable label for Previous Day tables
    const getClickableLabelColumns = (reportCategory, dataType) => {
        // Get data array based on reportCategory and dataType
        let dataArray = [];
        if (dataType === "today") {
            if (reportCategory === "Shipment Counts") {
                dataArray = shipmentCountsToday;
            } else if (reportCategory === "Delivery Data") {
                dataArray = deliveryDataToday;
            }
        } else {
            // For range type
            if (reportCategory === "Shipment Counts") {
                dataArray = shipmentCountsRange;
            } else if (reportCategory === "Delivery Data") {
                dataArray = deliveryDataRange;
            }
        }

        // Extract all labels from the data array, excluding "Total" and "Total Shipments"
        const allowedLabels = dataArray
            .map(item => item.label)
            .filter(label => label !== "total" && label !== "FSC Charge" && label !== "Delivery Percentage");

        return [
            {
                title: "",
                dataIndex: "label",
                key: "label",
                width: "60%",
                render: (label, record) => {
                    const isClickable = allowedLabels.includes(label);

                    // Make "totalDelivery" (second-to-last) bold - row highlighting is handled by rowClassName
                    if (label === "Total") {
                        return <strong>{label}</strong>;
                    }

                    if (record.label === "Total" || !isClickable) {
                        return <span>{label}</span>;
                    }

                    return (
                        <span
                            onClick={() => handleTableRowClick(record, reportCategory, dataType)}
                            style={{
                                cursor: "pointer",
                                color: "#1890ff",
                                textDecoration: "none",
                            }}
                        >
                            {label}
                        </span>
                    );
                },
            },
            {
                title: "",
                dataIndex: "value",
                key: "value",
                width: "40%",
                align: "right",
            },
        ];
    };

    return (
        <div className="director-dashboard">
            <div className="director-dashboard-content">
                {/* KPI Cards Section */}
                <div className="kpi-cards-row">
                    <div className="kpi-card-wrapper">
                        <Card className="kpi-card kpi-card-purple">
                            {kpiDataLoading ? (
                                <div className="kpi-card-content" style={{ padding: "12px 16px" }}>
                                    <Skeleton.Avatar active size={40} shape="circle" style={{ marginRight: 16 }} />
                                    <div style={{ flex: 1 }}>
                                        <Skeleton.Input active size="small" style={{ width: "80%", marginBottom: 8, height: 16 }} />
                                        <Skeleton.Input active size="large" style={{ width: "50%", height: 24 }} />
                                    </div>
                                </div>
                            ) : (
                                <div className="kpi-card-content">
                                    <div className="kpi-card-icon">
                                        <HubInscanShipmentsIcon />
                                    </div>
                                    <div className="kpi-card-info">
                                        <div className="kpi-card-title">Hub Inscan Shipments</div>
                                        <div className="kpi-card-value">{formatIndianNumber(kpiData.todayHubInscanShipments)}</div>
                                    </div>
                                </div>
                            )}
                        </Card>

                    </div>
                    <div className="kpi-card-wrapper">
                        <Card className="kpi-card kpi-card-purple">
                            {kpiDataLoading ? (
                                <div className="kpi-card-content" style={{ padding: "12px 16px" }}>
                                    <Skeleton.Avatar active size={40} shape="circle" style={{ marginRight: 16 }} />
                                    <div style={{ flex: 1 }}>
                                        <Skeleton.Input active size="small" style={{ width: "80%", marginBottom: 8, height: 16 }} />
                                        <Skeleton.Input active size="large" style={{ width: "50%", height: 24 }} />
                                    </div>
                                </div>
                            ) : (
                                <div className="kpi-card-content">
                                    <div className="kpi-card-icon">
                                        <HubInscanBagIcon />
                                    </div>
                                    <div className="kpi-card-info">
                                        <div className="kpi-card-title">Hub Inscan Bags</div>
                                        <div className="kpi-card-value">{formatIndianNumber(kpiData.todayHubInscanBags)}</div>
                                    </div>
                                </div>
                            )}
                        </Card>

                    </div>
                    <div className="kpi-card-wrapper">
                        <Card className="kpi-card kpi-card-purple">
                            {kpiDataLoading ? (
                                <div className="kpi-card-content" style={{ padding: "12px 16px" }}>
                                    <Skeleton.Avatar active size={40} shape="circle" style={{ marginRight: 16 }} />
                                    <div style={{ flex: 1 }}>
                                        <Skeleton.Input active size="small" style={{ width: "80%", marginBottom: 8, height: 16 }} />
                                        <Skeleton.Input active size="large" style={{ width: "50%", height: 24 }} />
                                    </div>
                                </div>
                            ) : (
                                <div className="kpi-card-content">
                                    <div className="kpi-card-icon">
                                        <HubOutscanShipmentsIcon />
                                    </div>
                                    <div className="kpi-card-info">
                                        <div className="kpi-card-title">Hub Outscan Shipments</div>
                                        <div className="kpi-card-value">{formatIndianNumber(kpiData.todayHubOutscanShipments)}</div>
                                    </div>
                                </div>
                            )}
                        </Card>

                    </div>
                    <div className="kpi-card-wrapper">
                        <Card className="kpi-card kpi-card-purple">
                            {kpiDataLoading ? (
                                <div className="kpi-card-content" style={{ padding: "12px 16px" }}>
                                    <Skeleton.Avatar active size={40} shape="circle" style={{ marginRight: 16 }} />
                                    <div style={{ flex: 1 }}>
                                        <Skeleton.Input active size="small" style={{ width: "80%", marginBottom: 8, height: 16 }} />
                                        <Skeleton.Input active size="large" style={{ width: "50%", height: 24 }} />
                                    </div>
                                </div>
                            ) : (
                                <div className="kpi-card-content">
                                    <div className="kpi-card-icon">
                                        <HubOutscanBagIcon />
                                    </div>
                                    <div className="kpi-card-info">
                                        <div className="kpi-card-title">Hub Outscan Bags</div>
                                        <div className="kpi-card-value">{formatIndianNumber(kpiData.todayHubOutscanBags)}</div>
                                    </div>
                                </div>
                            )}
                        </Card>

                    </div>
                    <div className="kpi-card-wrapper">
                        <Card className="kpi-card kpi-card-purple">
                            {kpiDataLoading ? (
                                <div className="kpi-card-content" style={{ padding: "12px 16px" }}>
                                    <Skeleton.Avatar active size={40} shape="circle" style={{ marginRight: 16 }} />
                                    <div style={{ flex: 1 }}>
                                        <Skeleton.Input active size="small" style={{ width: "80%", marginBottom: 8, height: 16 }} />
                                        <Skeleton.Input active size="large" style={{ width: "50%", height: 24 }} />
                                    </div>
                                </div>
                            ) : (
                                <div className="kpi-card-content">
                                    <div className="kpi-card-icon">
                                        <HubBagFinalizeIcon />
                                    </div>
                                    <div className="kpi-card-info">
                                        <div className="kpi-card-title">Hub Bag Finalized</div>
                                        <div className="kpi-card-value">{formatIndianNumber(kpiData.todayHubFinalizeBags)}</div>
                                    </div>
                                </div>
                            )}
                        </Card>

                    </div>
                </div>

                {/* Data Tables Section */}
                <Row gutter={[24, 24]} className="data-tables-row">
                    {/* Shipment Counts Column */}
                    <Col xs={24} lg={12}>
                        {/* Shipment Counts - Today */}
                        <Card
                            className="data-overview-card"
                            title={
                                <div className="data-table-header">
                                    <div className="header-title-group">
                                        <span className="box-title">Shipment Count</span>
                                        <span className="box-subtitle">{shipmentTodayDate}</span>
                                    </div>
                                    <button onClick={(e) => handleViewZoneWise(e, "today", "Piece Rate", "Shipment Counts")} className="view-zone-btn">
                                        View Zone Wise →
                                    </button>
                                </div>
                            }
                        >
                            {shipmentCountsPreviousDayLoading ? (
                                <Skeleton active paragraph={{ rows: 5 }} />
                            ) : (
                                <div className="data-grid">
                                    {shipmentCountsToday.map((item) => (
                                        <div
                                            key={item.key}
                                            className={`data-grid-item ${item.label === "Total" ? "total-item" : "clickable"}`}
                                            onClick={() => item.label !== "Total" && handleTableRowClick(item, "Shipment Counts", "today")}
                                            style={{ cursor: item.label !== "Total" ? "pointer" : "default" }}
                                        >
                                            <div className="item-label">{item.label}</div>
                                            <div className={`item-value ${item.label === "Total" ? "highlight" : ""}`}>{item.value}</div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </Card>


                        {/* Shipment Counts - Range */}
                        <Card
                            className="data-overview-card"
                            title={
                                <div className="data-table-header">
                                    <div className="header-title-group">
                                        <span className="box-title">Shipment Count</span>
                                        <span className="box-subtitle">{shipmentDateRange}</span>
                                    </div>
                                    <button onClick={(e) => handleViewZoneWise(e, "range", "Piece Rate", "Shipment Counts")} className="view-zone-btn">
                                        View Zone Wise →
                                    </button>
                                </div>
                            }
                        >
                            {shipmentCountsCurrentMonthLoading ? (
                                <Skeleton active paragraph={{ rows: 5 }} />
                            ) : (
                                <div className="data-grid">
                                    {shipmentCountsRange.map((item) => (
                                        <div
                                            key={item.key}
                                            className={`data-grid-item ${item.label === "Total" ? "total-item" : "clickable"}`}
                                            onClick={() => item.label !== "Total" && handleTableRowClick(item, "Shipment Counts", "range")}
                                            style={{ cursor: item.label !== "Total" ? "pointer" : "default" }}
                                        >
                                            <div className="item-label">{item.label}</div>
                                            <div className={`item-value ${item.label === "Total" ? "highlight" : ""}`}>{item.value}</div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </Card>

                    </Col>

                    {/* Delivery Data Column */}
                    <Col xs={24} lg={12}>
                        {/* Delivery Data - Today */}
                        <Card
                            className="data-overview-card"
                            title={
                                <div className="data-table-header">
                                    <div className="header-title-group">
                                        <span className="box-title">Delivery Data</span>
                                        <span className="box-subtitle">{deliveryDataTodayTimeZone || todayDate}</span>
                                    </div>
                                    <button onClick={(e) => handleViewZoneWise(e, "today", "Piece Rate", "Delivery Data")} className="view-zone-btn">
                                        View Zone Wise →
                                    </button>
                                </div>
                            }
                        >
                            {deliveryDataPreviousDayLoading ? (
                                <Skeleton active paragraph={{ rows: 5 }} />
                            ) : (
                                <div className="delivery-list">
                                    {deliveryDataToday.map((item) => (
                                        <div key={item.key} className={`delivery-item ${(item.label !== "Total" && item.label !== "Total DRS") ? "clickable" : ""}`}
                                            onClick={() => item.label !== "Total" && item.label !== "Total DRS" && handleTableRowClick(item, "Delivery Data", "today")}
                                            style={{ cursor: (item.label !== "Total" && item.label !== "Total DRS") ? "pointer" : "default" }}
                                        >
                                            <div className="d-label">{item.label}</div>
                                            <div className="d-value">{item.value}</div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </Card>

                    </Col>
                </Row>
            </div>
        </div>
    );
};

export default CentralOpsDashboard;
